// @ts-nocheck
/*
 * Port of v1 views/dashboard/components/CallQualityIndicator.js (simple view).
 *
 * Compact bar badge on the on-call header. The popover now shows four plain
 * readings — Audio drops / Jitter / Buffer / Delay — each with a one-line
 * explanation, plus the single worst warning. MOS, per-direction split,
 * bitrate, mic level, codec, candidate path and network type are still
 * sampled into the store and stored server-side; they are just not shown.
 * Reads useCallStore.callQuality + callQualityWarnings, both populated by
 * startWebrtcStatsMonitor's onSample callback (see CTIAgentUI orchestrator).
 */
import React, { useMemo, useState } from 'react';
import { useCallStore } from '@/router/zustand/callStore';
import { SIMPLE_DESC } from '@/utilities/callQualityRating';

const LABEL_COLORS: any = {
  excellent: '#10b981',
  good: '#22c55e',
  fair: '#f59e0b',
  poor: '#f97316',
  bad: '#ef4444',
  unknown: '#9ca3af',
};

// What the agent sees on the badge — plain words, not MOS buckets.
const LABEL_TEXT: any = {
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Okay',
  poor: 'Poor',
  bad: 'Bad',
  unknown: '—',
};

const SEVERITY_COLORS: any = {
  critical: '#ef4444',
  warning: '#f59e0b',
  info: '#6b7280',
};

const fmt = (v: any, unit = '', digits = 1) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const n = typeof v === 'number' ? Number(v.toFixed(digits)) : v;
  return `${n}${unit}`;
};

function Bars({ bars, color }: any) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 2, height: 14 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          style={{
            width: 3,
            height: 3 + i * 2.2,
            borderRadius: 1,
            background: i <= bars ? color : '#81818155',
            transition: 'background 200ms ease',
          }}
        />
      ))}
    </span>
  );
}

/** One live reading: name, number, and a line saying what it means. */
function Metric({ label, value, desc }: any) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
        <span style={{ opacity: 0.8 }}>{label}</span>
        <span style={{ fontWeight: 700 }}>{value}</span>
      </div>
      <div style={{ fontSize: 10, opacity: 0.6, lineHeight: 1.35 }}>{desc}</div>
    </div>
  );
}

function CallQualityIndicator({ compact = false }: any) {
  const quality = useCallStore((state: any) => state.callQuality);
  const warnings = useCallStore((state: any) => state.callQualityWarnings);
  const [open, setOpen] = useState(false);

  const worstWarning = useMemo(() => {
    if (!Array.isArray(warnings) || warnings.length === 0) return null;
    return (
      warnings.find((w: any) => w.severity === 'critical') ||
      warnings.find((w: any) => w.severity === 'warning') ||
      null
    );
  }, [warnings]);

  if (!quality) return null;

  const label = quality.label || 'unknown';
  const color = LABEL_COLORS[label] || LABEL_COLORS.unknown;
  const text = LABEL_TEXT[label] || LABEL_TEXT.unknown;
  const inb = quality.inbound || {};
  const outb = quality.outbound || {};

  // Worst of the two directions — the agent only cares that audio is dropping.
  const lossValues = [inb.lossPercent, outb.lossPercent].filter(
    (v: any) => v !== null && v !== undefined && !Number.isNaN(v),
  );
  const loss = lossValues.length ? Math.max(...lossValues) : null;
  const jitterValues = [inb.jitterMs, outb.jitterMs].filter(
    (v: any) => v !== null && v !== undefined && !Number.isNaN(v),
  );
  const jitter = jitterValues.length ? Math.max(...jitterValues) : null;

  return (
    <div
      className="call-quality-indicator"
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={() => setOpen((v) => !v)}
      title={worstWarning ? worstWarning.message : `Call quality: ${text}`}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '3px 8px',
          borderRadius: 999,
          border: `1px solid ${color}66`,
          background: `${color}14`,
          cursor: 'pointer',
          lineHeight: 1,
        }}
      >
        <Bars bars={quality.bars || 0} color={color} />
        {!compact && <span style={{ fontSize: 11, fontWeight: 600, color }}>{text}</span>}
        {worstWarning && (
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: SEVERITY_COLORS[worstWarning.severity] || SEVERITY_COLORS.info,
            }}
          />
        )}
      </span>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 60,
            minWidth: 'min(250px, calc(100vw - 24px))',
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid #81818144',
            background: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            textAlign: 'left',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700 }}>Call Quality</span>
            <span style={{ fontSize: 12, fontWeight: 700, color }}>{text}</span>
          </div>

          <Metric label="Audio drops" value={fmt(loss, '%', 1)} desc={SIMPLE_DESC.loss} />
          <Metric label="Jitter" value={fmt(jitter, ' ms')} desc={SIMPLE_DESC.jitter} />
          <Metric
            label="Buffer"
            value={fmt(inb.jitterBufferDelayMs, ' ms')}
            desc={SIMPLE_DESC.buffer}
          />
          <Metric label="Delay" value={fmt(quality.rttMs, ' ms')} desc={SIMPLE_DESC.delay} />

          {worstWarning && (
            <div
              style={{
                marginTop: 2,
                borderTop: '1px solid #81818133',
                paddingTop: 6,
                fontSize: 11,
                lineHeight: 1.4,
                color: SEVERITY_COLORS[worstWarning.severity] || SEVERITY_COLORS.info,
              }}
            >
              {worstWarning.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default React.memo(CallQualityIndicator);
